<template>
  <div class="vehicles">
    <header class="section-header">
      <div>
        <h1>Vehículos</h1>
        <p class="muted">
          Gestiona tus vehículos y accede a su historial de mantenimiento y documentos.
        </p>
      </div>
      <button class="primary" @click="openModal()">+ Nuevo vehículo</button>
    </header>

    <section v-if="loading" class="vehicles-grid skeleton-vehicles" role="status" aria-live="polite">
      <span class="sr-only">Cargando vehículos...</span>
      <div v-for="n in 3" :key="`vehicle-skel-${n}`" class="vehicle-card">
        <div class="vehicle-media skeleton"></div>
        <div class="vehicle-body">
          <div class="skeleton skeleton-line lg" style="width: 70%"></div>
          <div class="skeleton skeleton-line" style="width: 52%"></div>
          <div class="vehicle-tags">
            <div class="skeleton skeleton-line" style="width: 72px; height: 22px"></div>
            <div class="skeleton skeleton-line" style="width: 56px; height: 22px"></div>
          </div>
        </div>
      </div>
    </section>
    <section v-if="error" class="state error">{{ error }}</section>

    <section v-if="!loading" class="vehicles-grid">
      <div v-for="vehicle in vehicles" :key="vehicle.id" class="vehicle-card">
        <div class="vehicle-media" @click="openDetail(vehicle)">
          <img v-if="vehicle.cover" :src="vehicle.cover" :alt="vehicleLabel(vehicle)" />
          <div v-else class="vehicle-fallback">{{ typeLabel(vehicle.type) }}</div>
        </div>
        <div class="vehicle-body" @click="openDetail(vehicle)">
          <h3>{{ vehicleLabel(vehicle) }}</h3>
          <p class="muted small">{{ vehicleMeta(vehicle) }}</p>
          <div class="vehicle-tags">
            <span class="chip">{{ typeLabel(vehicle.type) }}</span>
            <span v-if="vehicle.registrationYear" class="chip ghost">
              {{ vehicle.registrationYear }}
            </span>
          </div>
        </div>
      </div>
      <div v-if="!vehicles.length" class="empty-card">
        <p>No hay vehículos todavía.</p>
        <button class="ghost" @click="openModal()">Crear el primero</button>
      </div>
    </section>

    <div v-if="showModal" class="modal-backdrop" @click.self="closeModal">
      <div class="modal">
        <header>
          <h3>{{ form.id ? "Editar vehículo" : "Nuevo vehículo" }}</h3>
          <button class="icon danger" @click="closeModal">×</button>
        </header>
        <div class="modal-body">
          <div class="row">
            <label>
              Tipo
              <select v-model="form.type">
                <option v-for="type in vehicleTypes" :key="type" :value="type">
                  {{ typeLabel(type) }}
                </option>
              </select>
            </label>
            <label>
              Año de matriculación
              <input v-model.number="form.registrationYear" type="number" min="1900" max="2100" />
            </label>
          </div>
          <label>
            Marca
            <input v-model="form.brand" type="text" placeholder="Seat, Yamaha..." />
          </label>
          <label>
            Modelo
            <input v-model="form.model" type="text" placeholder="Ibiza, MT-07..." />
          </label>
          <div class="row">
            <label>
              Matrícula
              <input v-model="form.plate" type="text" placeholder="0000-ABC" />
            </label>
            <label>
              Número de bastidor
              <input v-model="form.vin" type="text" placeholder="VIN" />
            </label>
          </div>
          <label>
            Fotos
            <div
              class="photo-dropzone"
              role="button"
              tabindex="0"
              @click="openPhotoDialog"
              @keydown.enter.prevent="openPhotoDialog"
              @keydown.space.prevent="openPhotoDialog"
            >
              <span class="photo-dropzone__plus">+</span>
              <span class="photo-dropzone__text">Subir fotos</span>
            </div>
            <input
              ref="photoInput"
              type="file"
              accept="image/*"
              multiple
              class="sr-only"
              @change="onPhotosSelected"
            />
          </label>
          <div v-if="selectedPhotos.length" class="ticket-preview">
            <p class="muted">Fotos nuevas</p>
            <div class="ticket-preview-grid">
              <div v-for="(photo, index) in selectedPhotos" :key="photo.id" class="ticket-preview-item">
                <img
                  :src="photo.preview"
                  :alt="photo.name"
                  @click="openLightbox(photo.preview, photo.name)"
                />
                <button class="ticket-remove" @click.stop="removePendingPhoto(index)">×</button>
              </div>
            </div>
          </div>
          <div v-if="existingPhotos.length" class="ticket-preview">
            <p class="muted">Fotos existentes</p>
            <div class="ticket-preview-grid">
              <div v-for="photo in existingPhotos" :key="photo.id" class="ticket-preview-item">
                <img
                  :src="photo.data"
                  :alt="photo.name"
                  @click="openLightbox(photo.data, photo.name)"
                />
                <button class="ticket-remove" @click.stop="removeExistingPhoto(form.id, photo.id)">×</button>
              </div>
            </div>
          </div>
          <p v-if="formError" class="form-error">{{ formError }}</p>
        </div>
        <footer>
          <button class="ghost" @click="closeModal">Cancelar</button>
          <button class="primary" @click="saveVehicle">{{ form.id ? "Guardar" : "Crear" }}</button>
        </footer>
      </div>
    </div>

    <div v-if="lightboxSrc" class="lightbox" @click.self="closeLightbox">
      <button class="lightbox-close" @click="closeLightbox">×</button>
      <img :src="lightboxSrc" :alt="lightboxAlt" />
    </div>
  </div>
</template>

<script>
import { fetchVehicles, createVehicle, updateVehicle, deleteVehiclePhoto } from "../services/vehicles";

export default {
  name: "VehiclesView",
  data() {
    return {
      vehicles: [],
      loading: false,
      error: "",
      showModal: false,
      formError: "",
      form: this.emptyForm(),
      selectedPhotos: [],
      existingPhotos: [],
      vehicleTypes: ["CAR", "MOTORCYCLE", "VAN", "TRUCK", "OTHER"],
      lightboxSrc: "",
      lightboxAlt: "",
    };
  },
  async mounted() {
    await this.loadVehicles();
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        type: "CAR",
        registrationYear: "",
        brand: "",
        model: "",
        plate: "",
        vin: "",
      };
    },
    async loadVehicles() {
      this.loading = true;
      this.error = "";
      try {
        const data = await fetchVehicles();
        this.vehicles = (data || []).map((vehicle) => this.normalizeVehicle(vehicle));
      } catch (err) {
        this.error = err?.message || "No se pudieron cargar los vehículos.";
      } finally {
        this.loading = false;
      }
    },
    openDetail(vehicle) {
      this.$router.push(`/dashboard/maintenance/vehicles/${vehicle.id}`);
    },
    openModal(vehicle) {
      this.formError = "";
      this.resetPendingPhotos();
      if (this.$refs.photoInput) {
        this.$refs.photoInput.value = "";
      }
      if (vehicle) {
        this.form = {
          id: vehicle.id,
          type: vehicle.type,
          registrationYear: vehicle.registrationYear || "",
          brand: vehicle.brand || "",
          model: vehicle.model || "",
          plate: vehicle.plate || "",
          vin: vehicle.vin || "",
        };
        this.existingPhotos = vehicle.photos ? [...vehicle.photos] : [];
      } else {
        this.form = this.emptyForm();
        this.existingPhotos = [];
      }
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.formError = "";
      this.resetPendingPhotos();
      this.existingPhotos = [];
    },
    async saveVehicle() {
      const brand = this.form.brand.trim();
      const model = this.form.model.trim();
      if (!brand || !model) {
        this.formError = "Marca y modelo son obligatorios.";
        return;
      }
      try {
        const photos = await this.buildPhotoPayload();
        const payload = {
          type: this.form.type,
          registrationYear: this.form.registrationYear || null,
          brand,
          model,
          plate: this.form.plate || null,
          vin: this.form.vin || null,
          photos: photos.length ? photos : null,
        };
        if (this.form.id) {
          await updateVehicle(this.form.id, payload);
        } else {
          await createVehicle(payload);
        }
        this.closeModal();
        try {
          await this.loadVehicles();
        } catch (_) {
          // ignore refresh errors after successful save
        }
      } catch (err) {
        this.formError = err?.message || "No se pudo guardar el vehículo.";
      }
    },
    onPhotosSelected(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      const mapped = files.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        name: file.name,
        file,
        preview: URL.createObjectURL(file),
      }));
      this.selectedPhotos = [...this.selectedPhotos, ...mapped];
      event.target.value = "";
    },
    openPhotoDialog() {
      this.$refs.photoInput?.click();
    },
    removePendingPhoto(index) {
      const [removed] = this.selectedPhotos.splice(index, 1);
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }
    },
    resetPendingPhotos() {
      this.selectedPhotos.forEach((photo) => {
        if (photo.preview) {
          URL.revokeObjectURL(photo.preview);
        }
      });
      this.selectedPhotos = [];
    },
    async removeExistingPhoto(vehicleId, resourceId) {
      if (!vehicleId) return;
      try {
        await deleteVehiclePhoto(vehicleId, resourceId);
        this.existingPhotos = this.existingPhotos.filter((photo) => photo.id !== resourceId);
        this.vehicles = this.vehicles.map((item) =>
          item.id === vehicleId
            ? { ...item, photos: (item.photos || []).filter((photo) => photo.id !== resourceId) }
            : item
        );
      } catch (err) {
        this.formError = err?.message || "No se pudo eliminar la foto.";
      }
    },
    async buildPhotoPayload() {
      if (!this.selectedPhotos.length) return [];
      const mapped = await Promise.all(
        this.selectedPhotos.map(async (item) => ({
          name: item.name,
          type: "IMAGE",
          format: "BASE64",
          data: await this.fileToBase64(item.file),
        }))
      );
      return mapped;
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    normalizeVehicle(vehicle) {
      if (!vehicle) return vehicle;
      return {
        ...vehicle,
        registrationYear: vehicle.registrationYear ?? vehicle.registration_year,
        photos: vehicle.photos || [],
        cover: vehicle.photos?.[0]?.data || "",
      };
    },
    vehicleLabel(vehicle) {
      const brand = vehicle.brand || "";
      const model = vehicle.model || "";
      return `${brand} ${model}`.trim() || "Vehículo";
    },
    vehicleMeta(vehicle) {
      const plate = vehicle.plate ? `· ${vehicle.plate}` : "";
      return `${vehicle.vin || "Sin bastidor"} ${plate}`.trim();
    },
    typeLabel(type) {
      const map = {
        CAR: "Coche",
        MOTORCYCLE: "Moto",
        VAN: "Furgoneta",
        TRUCK: "Camión",
        OTHER: "Otro",
      };
      return map[type] || type;
    },
    openLightbox(src, alt) {
      if (!src) return;
      this.lightboxSrc = src;
      this.lightboxAlt = alt || "Imagen";
    },
    closeLightbox() {
      this.lightboxSrc = "";
      this.lightboxAlt = "";
    },
  },
};
</script>

<style scoped>
.vehicles {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.section-header h1 {
  margin: 0 0 6px;
}

.vehicles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
}

.vehicle-card {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 16px;
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 16px;
  box-shadow: var(--shadow);
  align-items: center;
}

.vehicle-media {
  width: 180px;
  height: 180px;
  border-radius: 14px;
  overflow: hidden;
  background: var(--panel-2);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.vehicle-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.photo-dropzone {
  height: 120px;
  border-radius: 16px;
  border: 2px dashed rgba(148, 163, 184, 0.4);
  background: rgba(15, 23, 42, 0.4);
  display: grid;
  place-items: center;
  gap: 6px;
  color: var(--muted);
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.photo-dropzone:hover {
  border-color: rgba(56, 189, 248, 0.6);
  background: rgba(56, 189, 248, 0.08);
}

.photo-dropzone__plus {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(148, 163, 184, 0.35);
  display: grid;
  place-items: center;
  font-size: 20px;
  color: var(--text);
}

.photo-dropzone__text {
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.vehicle-fallback {
  color: var(--muted);
  font-weight: 600;
}

.vehicle-body {
  display: grid;
  gap: 6px;
  cursor: pointer;
}

.vehicle-body h3 {
  margin: 0;
}

.vehicle-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

@media (max-width: 860px) {
  .vehicle-card {
    grid-template-columns: 140px 1fr;
    align-items: start;
  }

  .vehicle-media {
    width: 140px;
    height: 140px;
  }
}

.muted {
  color: var(--muted);
}

.small {
  font-size: 12px;
}

.primary {
  background: linear-gradient(120deg, var(--accent), #22c55e);
  color: #071022;
  border: none;
  padding: 10px 16px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
  min-width: 140px;
}

.ghost {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text);
  padding: 8px 12px;
  border-radius: 999px;
  cursor: pointer;
  min-width: 120px;
  text-align: center;
}

.ghost.danger {
  color: var(--danger);
  border-color: rgba(248, 113, 113, 0.4);
}

.chip {
  background: rgba(56, 189, 248, 0.2);
  border: 1px solid rgba(56, 189, 248, 0.2);
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: var(--text);
}

.chip.ghost {
  background: rgba(148, 163, 184, 0.12);
  border-color: rgba(148, 163, 184, 0.25);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(6, 10, 18, 0.65);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: 20;
}

.modal {
  width: min(520px, 100%);
  max-width: calc(100vw - 32px);
  background: var(--panel);
  border-radius: 20px;
  border: 1px solid var(--border);
  padding: 20px;
  display: grid;
  gap: 16px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  overflow-x: hidden;
}

.modal footer {
  display: flex;
  justify-content: flex-end;
  gap: 14px;
}

.modal header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  display: grid;
  gap: 12px;
}

.modal-body label {
  display: grid;
  gap: 6px;
  color: var(--muted);
  font-size: 14px;
  min-width: 0;
}

.modal-body input,
.modal-body textarea,
.modal-body select {
  background: var(--panel-2);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  color: var(--text);
  width: 100%;
  max-width: 100%;
}

.modal-body input[type="file"] {
  display: block;
  min-width: 0;
}

.row {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

.icon {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--muted);
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
}

.icon.danger {
  color: var(--danger);
}

.ticket-preview {
  display: grid;
  gap: 8px;
}

.ticket-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
}

.ticket-preview-item {
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.35);
  overflow: hidden;
  display: grid;
  place-items: center;
}

.ticket-preview-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 6px;
  display: block;
}

.ticket-remove {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 23, 42, 0.75);
  color: #fff;
  cursor: pointer;
}

.state {
  padding: 24px;
  text-align: center;
  color: var(--muted);
}

.state.error {
  color: var(--danger);
}

.form-error {
  margin: 0;
  color: var(--danger);
  font-size: 13px;
}

.empty-card {
  text-align: center;
  color: var(--muted);
}

.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(3, 6, 14, 0.85);
  display: grid;
  place-items: center;
  z-index: 40;
  padding: 24px;
}

.lightbox img {
  max-width: min(920px, 92vw);
  max-height: min(90vh, 720px);
  object-fit: contain;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.65);
  padding: 12px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.45);
}

.lightbox-close {
  position: absolute;
  top: 18px;
  right: 20px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(15, 23, 42, 0.8);
  color: #fff;
  font-size: 20px;
  cursor: pointer;
}
</style>
